import { IconButton, Box } from "@mui/material";
import { styled } from "@mui/material/styles";
import ReactPlayer, { ReactPlayerProps } from "react-player";
import FallbackWaveImage from "Images/Content/audio-waves.png";
import "./style.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMediaPlayer } from "JS/React/Hooks/MediaPlayer";
import {
    checkIfUrlIsBlob,
    getS3CompletePathFromPreSigned,
    isSameMediaSource,
} from "JS/Helpers";
import { useMiniPlayer } from "JS/React/Hooks/MiniPlayer";
import { MainPlayerOverlay } from "./MainPlayerOverlay";
import { RefreshButton } from "./RefreshButton";

interface StyledRootProps {
    imageURL?: string;
    showBackground?: boolean;
}

const StyledRoot = styled(Box, {
    shouldForwardProp: (prop) =>
        prop !== "imageURL" && prop !== "showBackground",
})<StyledRootProps>(({ theme, imageURL, showBackground }) => ({
    height: "inherit",
    display: "flex",
    justifyContent: "center",
    alignItems: "end",
    position: "relative",
    ...(showBackground && {
        backgroundImage: `url(${imageURL})`,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        backgroundPosition: "center center",
        backgroundColor: `${theme.palette.background.default}`,
    }),
}));

const StyledPlayIcon = styled(FontAwesomeIcon)(({ theme }) => ({
    fontSize: "40px",
    color: `${theme.colors.white}`,
}));

export const MediaPlayer = (props: MediaPlayerProps) => {
    const imageURL = useMemo(() => {
        if (!props.imageURL || props.imageURL === "a") {
            return FallbackWaveImage;
        }
        return props.imageURL;
    }, [props.imageURL]);

    const {
        isAudio,
        playerRef,
        playing: parentPlaying = true,
        title,
        isMini,
        mediaURL,
        onNext,
        onError,
        onPrevious,
        loading,
        onUpdateExpiredUrl: onPlayerError,
        onReady,
        ...rest
    } = props;

    const { miniPlayer } = useMiniPlayer();

    // playerRef is an optional prop; fall back so teardown can always reach the player
    const internalRef = useRef<any>(null);
    const activeRef = playerRef ?? internalRef;
    const mediaElementRef = useRef<any>(null);

    const {
        isError,
        contentUrl,
        maintainPlaying,
        isPlayerReady,
        handleMediaError,
        handlePlaybackRate,
        handleReady,
    } = useHandlers({ ...props, imageURL }, mediaElementRef);

    const playerHeight = isAudio ? "54px" : "100%";

    // no deps: React nulls the ref before cleanups, so re-read it each render
    useEffect(() => {
        const internal = activeRef.current?.getInternalPlayer?.();

        if (internal && internal !== mediaElementRef.current) {
            mediaElementRef.current?.pause?.();
            mediaElementRef.current = internal;
        }
    });

    useEffect(() => {
        return () => {
            // detaching a media element does not stop it — pause explicitly
            mediaElementRef.current?.pause?.();
            mediaElementRef.current = null;
        };
    }, []);

    const playerHidden = !isMini && !!miniPlayer.url;

    useEffect(() => {
        if (playerHidden) {
            mediaElementRef.current?.pause?.();
            mediaElementRef.current = null;
        }
    }, [playerHidden]);

    const [initialPlaying] = useState(parentPlaying);
    const [playing, setPlaying] = useState(parentPlaying);

    useEffect(() => {
        setPlaying(parentPlaying);
    }, [parentPlaying]);

    useEffect(() => {
        if (!isMini && !miniPlayer.url && isAudio) {
            setPlaying(initialPlaying);
        }
    }, [miniPlayer.url]);

    const playingState = useMemo(
        () =>
            isPlayerReady &&
            (playing || maintainPlaying || miniPlayer?.isPlaying),
        [miniPlayer?.isPlaying, playing, maintainPlaying, isPlayerReady],
    );

    const thumbnail = useMemo(() => {
        const condition =
            isAudio ||
            isMini ||
            miniPlayer.url ||
            miniPlayer?.isPlaying === true;
        if (condition) return false;

        return imageURL;
    }, [isMini, isAudio, playing, maintainPlaying, miniPlayer]);

    const showBackground = isAudio || (!isAudio && !isMini && !!miniPlayer.url);

    return (
        <StyledRoot
            imageURL={imageURL}
            showBackground={showBackground}
            onContextMenu={(e) => e.preventDefault()}
        >
            {loading && isAudio && !isMini && !miniPlayer.url && (
                <MainPlayerOverlay />
            )}

            {!isMini && isError && <RefreshButton />}

            {!isMini && miniPlayer.url ? (
                <></>
            ) : (
                <ReactPlayer
                    playbackRate={miniPlayer.playbackRate}
                    playsinline={true}
                    ref={activeRef}
                    width="100%"
                    height={playerHeight}
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                    url={contentUrl}
                    light={thumbnail}
                    playIcon={
                        <IconButton aria-label="Play">
                            <StyledPlayIcon icon={["fas", "play"]} />
                        </IconButton>
                    }
                    playing={playingState}
                    controls={
                        !isMini ? !isMini && !miniPlayer.url && !loading : false
                    }
                    onError={handleMediaError}
                    onPlaybackRateChange={handlePlaybackRate}
                    onReady={handleReady}
                    config={{
                        file: {
                            forceAudio: isAudio,
                            attributes: {
                                controlsList: "nodownload",
                                disablePictureInPicture: true,
                            },
                        },
                    }}
                    {...rest}
                />
            )}
        </StyledRoot>
    );
};

const useHandlers = (
    props: MediaPlayerProps,
    mediaElementRef?: React.RefObject<any>,
) => {
    const {
        mediaURL,
        isMini,
        onError = () => {},
        onUpdateExpiredUrl: onPlayerError,
        onPlaybackRateChange = () => {},
        onReady = () => {},
    } = props;
    const { maintainPlaying, onMediaPlayerError } = useMediaPlayer(isMini);
    const { setGlobalMiniPlayer } = useMiniPlayer();

    useMediaSession(props);

    const [contentUrl, setContentUrl] = useState(mediaURL);
    const [isError, setIsError] = useState<boolean>(false);

    const [isPlayerReady, setIsPlayerReady] = useState<boolean>(false);

    useEffect(() => {
        if (isSameMediaSource(contentUrl, mediaURL)) return;

        mediaElementRef?.current?.pause?.();
        setContentUrl(mediaURL);
    }, [mediaURL]);

    const onErr = (e: any) => {
        setIsError(true);
        onError(e);
    };

    const handleMediaError = (e) => {
        const isBlob = checkIfUrlIsBlob(contentUrl);
        onMediaPlayerError(
            e,
            !isBlob ? getS3CompletePathFromPreSigned(contentUrl) : null,
            setContentUrl,
            onPlayerError,
            onErr,
        );
    };

    const handlePlaybackRate = (rate: number) => {
        setGlobalMiniPlayer({
            playbackRate: rate,
        });
        onPlaybackRateChange(rate);
    };

    const handleReady = (player: ReactPlayer) => {
        setIsError(false);
        setIsPlayerReady(true);
        onReady(player);
    };

    return {
        maintainPlaying,
        contentUrl,
        isError,
        isPlayerReady,
        handleMediaError,
        handlePlaybackRate,
        handleReady,
    };
};

const useMediaSession = (props: MediaPlayerProps) => {
    const { title, imageURL } = props;
    const { miniPlayer } = useMiniPlayer();

    useEffect(() => {
        if ("mediaSession" in navigator) {
            const artwork = [];
            if (miniPlayer.url && miniPlayer.thumbnail) {
                artwork.push({
                    src: miniPlayer.thumbnail,
                    size: "719x1080",
                    type: "image/jpeg",
                });
            } else if (imageURL) {
                artwork.push({
                    src: imageURL,
                    size: "719x1080",
                    type: "image/jpeg",
                });
            }
            navigator.mediaSession.metadata = new MediaMetadata({
                title: miniPlayer.title ? miniPlayer.title : title,
                artwork,
            });
        }
    }, [title, miniPlayer]);
};

export interface MediaPlayerProps extends ReactPlayerProps {
    mediaURL: string;
    imageURL: string;
    isAudio?: boolean;
    playerRef?: any;
    title: string;
    isMini?: boolean;
    loading?: boolean;
    onNext?: () => void;
    onPrevious?: () => void;
    onUpdateExpiredUrl?: () => void;
    onPlaybackRateChange?: (rate: number) => void;
    onReady?: (player: ReactPlayer) => void;
}
